import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import {
  API_AMOUNT_SCALE,
  formatSomAmountInputMask,
  formatSomNumber,
} from "../utils/apiMoney";
import { getTablets, isBasicConfigured, postTransaction } from "../api/protected";
import { asArray } from "../poster/response";
import { toast } from "react-toastify";

const MIN_TOPUP_SOM = 10_000;

/**
 * @param {string} raw
 * @returns {number | null}
 */
function parseSomAmountInput(raw) {
  const cleaned = String(raw ?? "")
    .replace(/\s/g, "")
    .replace(",", ".");
  if (cleaned === "") {
    return null;
  }
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : null;
}

function WalletIcon({ size = 22 }) {
  const c = {
    width: size,
    height: size,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: 1.8,
    strokeLinecap: "round",
    strokeLinejoin: "round",
  };
  return (
    <svg {...c}>
      <path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" />
      <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4z" />
    </svg>
  );
}

/**
 * @param {{ show: boolean; onHide: () => void; accountUrl: string }} props
 */
export default function BalanceTopUpModal({ show, onHide, accountUrl }) {
  const [topUpAmount, setTopUpAmount] = useState(() =>
    formatSomAmountInputMask(String(MIN_TOPUP_SOM)),
  );
  const [topUpSubmitting, setTopUpSubmitting] = useState(false);

  const acc = (accountUrl || "").trim();
  const canTopUp = Boolean(acc) && isBasicConfigured();
  const parsedTopUp = parseSomAmountInput(topUpAmount);
  const topUpValid = parsedTopUp != null && parsedTopUp >= MIN_TOPUP_SOM;

  useEffect(() => {
    if (show) {
      setTopUpAmount(formatSomAmountInputMask(String(MIN_TOPUP_SOM)));
    }
  }, [show]);

  const handleClose = () => {
    if (topUpSubmitting) {
      return;
    }
    onHide();
  };

  const confirmTopUp = async () => {
    if (!topUpValid || parsedTopUp == null) {
      toast.error(
        `Укажите сумму не ниже ${formatSomNumber(MIN_TOPUP_SOM)} сум`,
      );
      return;
    }
    if (!canTopUp) {
      toast.error("Задайте учётные данные API и account_url");
      return;
    }
    setTopUpSubmitting(true);
    try {
      const amountMinor = Math.round(parsedTopUp * API_AMOUNT_SCALE);
      const raw = await postTransaction(acc, {
        amount: amountMinor,
      });
      const checkoutUrl =
        raw &&
        typeof raw === "object" &&
        "message" in /** @type {any} */ (raw) &&
        typeof (/** @type {any} */ (raw).message) === "string"
          ? /** @type {any} */ (raw).message.trim()
          : "";
      if (checkoutUrl && /^https?:\/\//i.test(checkoutUrl)) {
        onHide();
        window.open(checkoutUrl, "_blank");
        return;
      }
      toast.error("Сервер не вернул ссылку на оплату");
    } catch (e) {
      const ax = /** @type {any} */ (e);
      const apiMsg =
        ax?.response?.data &&
        typeof ax.response.data === "object" &&
        "message" in ax.response.data
          ? String(ax.response.data.message)
          : null;
      toast.error(apiMsg || ax?.message || "Не удалось создать платёж");
    } finally {
      setTopUpSubmitting(false);
    }
  };

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      backdrop={topUpSubmitting ? "static" : true}
      keyboard={!topUpSubmitting}
      dialogClassName="appModalDialog appModalDialog--topup"
      contentClassName="appModalContent"
      backdropClassName="appModalBackdrop"
    >
      <Modal.Header closeButton className="appModalContent__header">
        <div className="appModalContent__titleRow">
          <span className="appModalContent__titleIcon" aria-hidden>
            <WalletIcon size={22} />
          </span>
          <Modal.Title as="h3" className="appModalContent__title">
            Пополнение баланса
          </Modal.Title>
        </div>
      </Modal.Header>
      <Modal.Body className="appModalContent__body">
        <div className="appModalField">
          <label className="appModalField__label" htmlFor="billingTopUpAmount">
            Сумма, сум
          </label>
          <input
            id="billingTopUpAmount"
            className={[
              "appModalInput",
              topUpAmount.trim() !== "" &&
              (parsedTopUp == null || parsedTopUp < MIN_TOPUP_SOM)
                ? "appModalInput--invalid"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            type="text"
            inputMode="decimal"
            autoComplete="off"
            value={topUpAmount}
            onChange={(e) =>
              setTopUpAmount(formatSomAmountInputMask(e.target.value))
            }
            placeholder={formatSomAmountInputMask(String(MIN_TOPUP_SOM))}
            disabled={topUpSubmitting}
          />
          <p className="appModalField__hint">
            Минимум {formatSomNumber(MIN_TOPUP_SOM)} сум
          </p>
          {topUpAmount.trim() !== "" &&
            (parsedTopUp == null || parsedTopUp < MIN_TOPUP_SOM) && (
              <p className="appModalField__error" role="alert">
                Введите сумму не ниже {formatSomNumber(MIN_TOPUP_SOM)} сум
              </p>
            )}
        </div>
      </Modal.Body>
      <Modal.Footer className="appModalContent__footer">
        <button
          type="button"
          className="link-button link-button--sm link-button--secondary flex-grow-1"
          disabled={topUpSubmitting}
          onClick={handleClose}
        >
          Отмена
        </button>
        <button
          type="button"
          className="link-button link-button--sm flex-grow-1"
          disabled={!topUpValid || !canTopUp || topUpSubmitting}
          onClick={() => void confirmTopUp()}
        >
          {topUpSubmitting ? "Отправка…" : "Перейти к оплате"}
        </button>
      </Modal.Footer>
    </Modal>
  );
}
